type LogLevel = 'log' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any[];
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  private formatTimestamp(): string {
    const now = new Date();
    return now.toISOString();
  }

  private addLog(level: LogLevel, message: string, ...data: any[]) {
    const entry: LogEntry = {
      timestamp: this.formatTimestamp(),
      level,
      message,
      data: data.length > 0 ? data : undefined,
    };

    this.logs.push(entry);

    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console
    const consoleMethod = console[level] || console.log;
    consoleMethod(`[${entry.timestamp}] ${message}`, ...data);
  }

  log(message: string, ...data: any[]) {
    this.addLog('log', message, ...data);
  }

  info(message: string, ...data: any[]) {
    this.addLog('info', message, ...data);
  }

  warn(message: string, ...data: any[]) {
    this.addLog('warn', message, ...data);
  }

  error(message: string, ...data: any[]) {
    this.addLog('error', message, ...data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsAsText(): string {
    return this.logs
      .map((entry) => {
        const dataStr = entry.data ? ' ' + JSON.stringify(entry.data, null, 2) : '';
        return `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}${dataStr}`;
      })
      .join('\n');
  }

  downloadLogs(filename = 'auth-debug-logs.txt') {
    const text = this.getLogsAsText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  clear() {
    this.logs = [];
    console.clear();
  }

  // Save logs to localStorage for persistence across page reloads
  saveToPersistence() {
    try {
      localStorage.setItem('debug_logs', JSON.stringify(this.logs));
    } catch (e) {
      console.error('Failed to save logs to localStorage:', e);
    }
  }

  // Load logs from localStorage
  loadFromPersistence() {
    try {
      const saved = localStorage.getItem('debug_logs');
      if (saved) {
        this.logs = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load logs from localStorage:', e);
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Load any persisted logs on initialization
logger.loadFromPersistence();

// Auto-save logs periodically
setInterval(() => {
  logger.saveToPersistence();
}, 5000);

// Make logger available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).debugLogger = logger;
}

export default logger;
